import React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import {
  BaseCard,
  BaseCardHeader,
  Icon,
  COMMON_COLORS,
  Tag,
  DialogPopup,
  SkeletonWrapper,
} from '@neteera/neteera-web-nextgen';

import { deepEqual } from 'src/utils/comparators';
import Connector, {
  PropsFromRedux,
  APNEA_SCORE,
  AHI_SCORE_THRESHOLD,
} from './Connector';
import { Wrapper, ApneaScoreValue, LeftWrapper, RightWrapper } from './styled';
import TrendingOverviewChart from './TrendingOverviewChart';
import {
  getApneaScoreDescription,
  getApneaScoreValue,
  getTrendIconName,
  getTrendLabel,
  messages,
} from './utils';

type Props = PropsFromRedux & {
  intl: IntlShape;
};

const SleepApnea = ({
  intl,
  apneaScore,
  startOfNight,
  endOfNight,
  failureReason,
  trend,
  sleepData,
  sleepDataTimeFrame,
  isLoading,
}: Props) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  return (
    <BaseCard>
      <BaseCardHeader
        title={intl.formatMessage(messages.score)}
        leftIconProps={{
          iconName: 'Conditions',
        }}
        size="base"
        isLoading={isLoading}
      >
        <Icon
          className="cursor-pointer"
          iconName="Expand"
          onClick={() => setIsDialogOpen(true)}
        />
      </BaseCardHeader>
      <SkeletonWrapper isLoading={isLoading} className="mt-2" full>
        <Wrapper>
          <LeftWrapper>
            <ApneaScoreValue
              className="text-lg font-normal"
              color={
                apneaScore === APNEA_SCORE.ABOVE
                  ? COMMON_COLORS.yellowDark
                  : 'inherit'
              }
            >
              {getApneaScoreValue(apneaScore, intl)}
            </ApneaScoreValue>
            <span className="text-grey-600 mt-1 font-normal">
              {getApneaScoreDescription(apneaScore, intl, failureReason)}
            </span>
            <span className="text-neteera-teal-500 text-xs font-medium">
              {startOfNight} - {endOfNight}
            </span>
          </LeftWrapper>
          <RightWrapper>
            <Tag
              label={getTrendLabel(trend, intl)}
              size="lg"
              className="py-5 text-sm font-medium"
              iconProps={{
                iconName: getTrendIconName(trend),
              }}
              bgColor={
                trend === APNEA_SCORE.ABOVE
                  ? (COMMON_COLORS.yellowLight as string)
                  : (COMMON_COLORS.blueLight as string)
              }
            />
          </RightWrapper>
        </Wrapper>
      </SkeletonWrapper>
      <DialogPopup
        open={isDialogOpen}
        onOpenChange={() => null}
        handleClose={() => setIsDialogOpen(false)}
        size="md"
        titleSize="base"
      >
        <TrendingOverviewChart
          sleepData={sleepData}
          sleepDataTimeFrame={sleepDataTimeFrame}
          ahiScoreThreshold={AHI_SCORE_THRESHOLD}
        />
      </DialogPopup>
    </BaseCard>
  );
};

export default Connector(
  injectIntl(
    React.memo(SleepApnea, (oldProps, newProps) =>
      deepEqual(oldProps, newProps),
    ),
  ),
);
